import { Section } from '../components/Section';
import {
  FluviometricPost,
  fluviometricMap,
  fluviometricPostGroups,
} from '../constants/postos-fluviometricos';
import styles from '../styles/estacoes.module.css';

function getGroupId(title: string) {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-');
}

function FluviometricCard({ post }: { post: FluviometricPost }) {
  const media = post.embedUrl ? (
    <iframe
      className={styles.stationCardFrame}
      src={post.embedUrl}
      title={post.alt || post.title}
      loading="lazy"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
    />
  ) : post.image ? (
    <img src={post.image} alt={post.alt || post.title} />
  ) : (
    <div className={styles.stationCardPlaceholder}>
      <i className="bi bi-camera-video" aria-hidden />
    </div>
  );

  const body = (
    <div className={styles.stationCardBody}>
      <div>
        {post.status && (
          <span className={styles.statusBadge}>{post.status}</span>
        )}
        <h3>{post.title}</h3>
        {post.description && <p>{post.description}</p>}

        {post.source && (
          <p>
            <strong>Fonte:</strong> {post.source}
          </p>
        )}

        {post.embedUrl && (
          <span className={styles.inlineAction}>
            Acessar câmera <i className="bi bi-arrow-up-right" aria-hidden />
          </span>
        )}
      </div>
    </div>
  );

  const content = (
    <>
      {media}
      {body}
    </>
  );

  if (post.embedUrl && post.link) {
    return (
      <div
        className={`${styles.stationCard} ${styles.stationCardPending} ${styles.stationCardInteractive}`}
      >
        {content}
        <a
          href={post.link}
          className={styles.stationCardOverlayLink}
          target="_blank"
          rel="noreferrer"
          aria-label={`Abrir ${post.title} - ${post.description || ''}`}
        />
      </div>
    );
  }

  if (!post.link) {
    return (
      <div className={`${styles.stationCard} ${styles.stationCardPending}`}>
        {content}
      </div>
    );
  }

  return (
    <a
      href={post.link}
      className={styles.stationCard}
      target="_blank"
      rel="noreferrer"
    >
      {content}
    </a>
  );
}

export default function PostosFluviometricos() {
  const postCount = fluviometricPostGroups.reduce(
    (total, group) => total + group.posts.length,
    0,
  );

  return (
    <Section className={styles.page}>
      <div className="col-12">
        <header className={styles.hero}>
          <div>
            <span className={styles.eyebrow}>Rede de monitoramento</span>
            <h1>Níveis dos rios e arroios</h1>
            <p>
              Acompanhe câmeras de videomonitoramento, sensores de nível por radar e réguas de nível que apoiam o monitoramento dos rios e arroios em Rolante/RS.
            </p>
          </div>
          <div className={styles.heroStats} aria-label="Resumo dos pontos de monitoramento">
            <strong>{postCount}</strong>
            <span>pontos cadastrados</span>
          </div>
        </header>
      </div>

      <div className="col-12">
        <section className={styles.overview}>
          {fluviometricMap.link && (
            <a
              href={fluviometricMap.link}
              className={styles.mapFeature}
              target="_blank"
              rel="noreferrer"
            >
              {fluviometricMap.image && (
                <img
                  src={fluviometricMap.image}
                  alt={fluviometricMap.alt || fluviometricMap.title}
                />
              )}
              <div>
                <span className={styles.eyebrow}>Mapa interativo</span>
                <h2>{fluviometricMap.title}</h2>
                <p>{fluviometricMap.description}</p>
                <span className={styles.inlineAction}>
                  Abrir mapa <i className="bi bi-arrow-up-right" aria-hidden />
                </span>
              </div>
            </a>
          )}

          <div className={styles.citySummary}>
            <div className={styles.sectionHeader}>
              <div>
                <span className={styles.eyebrow}>Categorias</span>
                <h2>Cobertura dos pontos</h2>
              </div>
            </div>

            {fluviometricPostGroups.map((group) => (
              <a
                href={`#${getGroupId(group.title)}`}
                className={styles.citySummaryItem}
                key={group.title}
              >
                <span>{group.title}</span>
                <strong>{group.posts.length}</strong>
              </a>
            ))}
          </div>
        </section>
      </div>

      {fluviometricPostGroups.map((group) => (
        <section
          className={`col-12 ${styles.cityBlock}`}
          key={group.title}
          id={getGroupId(group.title)}
        >
          <div className={styles.sectionHeader}>
            <div>
              <span className={styles.eyebrow}>{group.eyebrow}</span>
              <h2>{group.title}</h2>
              <p className={styles.sectionDescription}>{group.description}</p>
            </div>
          </div>

          <div className={styles.stationGrid}>
            {group.posts.map((post, index) => (
              <FluviometricCard
                key={`${group.title}-${post.title}-${index}`}
                post={post}
              />
            ))}
          </div>
        </section>
      ))}
    </Section>
  );
}
